/* eslint-disable complexity */
import React, { useEffect, useState } from 'react';
import {
  Icon,
  SegmentedButtons,
  SelectMenu,
  Spinner,
  TextField,
  Tooltip,
  Typography,
} from '@scale/scaleui';
import CopyToClipboard from 'react-copy-to-clipboard';
import { PodType } from '@scale/shared-types';
import {
  IOnboardingFlow,
  OnboardingFlowNodeType,
  SimpleStepStatsResponse,
  UserPathTimeSpentSteps,
  getTopologicallySortedNodesFromFlow,
  OnboardingFlowNode,
} from '@scale/shared/onboardingControlPanel';
import PanelEntry from 'react-scale/components/GrowthCenter/OnboardingControlPanel/components/PanelEntry';
import {
  getFlowStats,
  getStepAvgTimeSpentWithinTimeFrame,
  getStepConversionRateWithinTimeFrame,
  updateContributorFacingDescription,
  updateContributorFacingName,
  updatePodType,
  fetchUserPathMetrics,
  getAllVersionsConversionData,
  getFlowById,
} from 'react-scale/components/GrowthCenter/OnboardingControlPanel/api';
import { toast } from 'react-toastify';
import { useOCPStore } from 'react-scale/components/GrowthCenter/OnboardingControlPanel/hooks/useOCPStore';
import moment from 'moment';
import { useDraftFlowStore } from '../../hooks/useDraftFlowStore';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

interface DetailPanelFlowOverviewProps {
  currentFlow: IOnboardingFlow;
  currentTab: string;
}

interface StepData {
  name: string;
  averageTimeSpentData: SimpleStepStatsResponse;
  conversionRateData: SimpleStepStatsResponse;
}

const conversionBarStyle = (percentage: number) => {
  return {
    width: `${Math.min(percentage, 100)}%`,
    height: '100%',
    backgroundColor: '#B9B2FF',
    color: '#1E2229',
    display: 'flex',
    justifyContent: 'flex-end',
  };
};

const DetailPanelFlowOverview: React.FC<DetailPanelFlowOverviewProps> = ({
  currentFlow,
  currentTab,
}) => {
  const [flowStats, setFlowStats] = useState({
    totalEnteredUsers: -1,
    totalExitedUsers: -1,
    totalActiveUsers: -1,
  });
  const [isLoadingFlowStats, setIsLoadingFlowStats] = useState<boolean>(false);
  const [isLoadingStepStats, setIsLoadingStepStats] = useState<boolean>(false);
  const [isLoadingVersionComparisons, setIsLoadingVersionComparisons] = useState<boolean>(false);
  const [isLoadingUserMetrics, setIsLoadingUserMetrics] = useState<boolean>(false);
  const [userTimeSpentSteps, setUserTimeSpentSteps] = useState<UserPathTimeSpentSteps>([]);
  const [stepData, setStepData] = useState<StepData[]>([]);
  const [contributorFacingFlowName, setContributorFacingFlowName] = useState<string>(
    currentFlow.externalName ? currentFlow.externalName : '',
  );
  const [contributorFacingFlowDescription, setContributorFacingFlowDescription] = useState<string>(
    currentFlow.externalDescription ? currentFlow.externalDescription : '',
  );

  const podTypeOptions = [...Object.values(PodType).map(value => ({ id: value, name: value }))] as {
    id: PodType;
    name: PodType;
  }[];

  const [podType, setPodType] = useState<PodType>(PodType.NotSet);

  useEffect(() => {
    // Define the async function inside the useEffect
    const fetchFlow = async () => {
      const flow = await getFlowById(currentFlow._id);
      if (flow && flow.podType) {
        setPodType(flow.podType);
      }
    };

    fetchFlow();
  }, [currentFlow._id, currentFlow.version]);

  const {
    currentVersion,
    setCurrentVersion,
    userTraversal,
    searchText,
    flowFilterTags,
    flowFilterDates,
  } = useOCPStore(state => ({
    currentVersion: state.currentVersion,
    setCurrentVersion: state.setCurrentVersion,
    userTraversal: state.userTraversal,
    searchText: state.searchText,
    flowFilterTags: state.flowFilterTags,
    flowFilterDates: state.flowFilterDates,
  }));
  const editMode = currentVersion === -1;

  const [stepPerformanceMetricsView, setStepPerformanceMetricsView] =
    useState<string>('Conversion');
  const [performanceComparisonVersion, setPerformanceComparisonVersion] = useState<number>(-1);
  const [comparisonData, setComparisonData] = useState<StepData[]>([]);

  // versions that have been up long enough to have conversion data
  const [releasedVersions, setReleasedVersions] = useState<{ id: number; name: string }[]>([]);
  // data for versions with conversion data
  const [conversionDataByVersion, setConversionDataByVersion] = useState<
    Record<number, StepData[]>
  >({});

  const handleStartEdit = () => {
    if (currentVersion !== -1) setCurrentVersion(-1);
  };

  useEffect(() => {
    (async () => {
      setIsLoadingFlowStats(true);
      try {
        const startDate = flowFilterDates[0].toISOString();
        const endDate = flowFilterDates[1].toISOString();
        const tagIds = flowFilterTags.map(tag => tag.value);
        const stats = await getFlowStats(currentFlow._id, startDate, endDate, tagIds);
        setFlowStats({
          totalEnteredUsers: stats.totalUsersEntered,
          totalExitedUsers: stats.totalUsersExited,
          totalActiveUsers: stats.totalUsers,
        });
      } catch (error) {
        toast(`Error retrieving flow stats: ${error.message}`, {
          type: 'error',
          autoClose: 5000,
          position: 'top-right',
        });
      } finally {
        setIsLoadingFlowStats(false);
      }
    })();
  }, [currentFlow._id, flowFilterDates, flowFilterTags]);

  // find versions with conversion data for comparison and set conversion data
  useEffect(() => {
    (async () => {
      setIsLoadingVersionComparisons(true);
      const versionsWithData: { id: number; name: string }[] = [];
      // maps version number to conversion data for that version
      const conversionData: Record<number, StepData[]> = {};

      const sortedNodes: OnboardingFlowNode[] = getTopologicallySortedNodesFromFlow(
        currentFlow,
      ).filter(n => n.type === OnboardingFlowNodeType.Step);
      try {
        const timeData = await getAllVersionsConversionData(
          currentFlow._id,
          currentFlow.nodes
            .filter(node => node.type === OnboardingFlowNodeType.Step)
            .map(node => node.name),
        );

        // finding versions with conversion data by checking which versions
        // have defined time/percent conversion data
        const versions: Set<number> = new Set();
        if (timeData.averageTimes) {
          for (const key in timeData.averageTimes) {
            versions.add(+key);
          }
        }
        if (timeData.percentConversion) {
          for (const key in timeData.percentConversion) {
            versions.add(+key);
          }
        }

        [...versions]
          .sort((a, b) => +a - +b)
          .forEach(version => {
            versionsWithData.push({ id: version, name: `v${version}` });
            const stepsAverageTime = timeData.averageTimes[version] ?? {};
            const stepsPercentConversion = timeData.percentConversion[version] ?? {};
            const sortedStepsData = [];
            for (const node of sortedNodes) {
              const name = node.name;
              const stepData: StepData = {
                name,
                averageTimeSpentData: {
                  result: stepsAverageTime[name],
                },
                conversionRateData: {
                  result: stepsPercentConversion[name],
                },
              };
              sortedStepsData.push(stepData);
            }
            conversionData[version] = sortedStepsData;
          });
      } catch (error) {
        toast(`Error retrieving step stats: ${error.message}`, {
          type: 'error',
          autoClose: 5000,
          position: 'top-right',
        });
      } finally {
        setReleasedVersions(versionsWithData);
        setConversionDataByVersion(conversionData);
        setPerformanceComparisonVersion(versionsWithData.length !== 0 ? versionsWithData[0].id : 0);
        setIsLoadingVersionComparisons(false);
      }
    })();
  }, [currentFlow, currentFlow._id]);

  useEffect(() => {
    (async () => {
      setIsLoadingStepStats(true);
      try {
        const tagIds = flowFilterTags.map(tag => tag.value);
        // Filter step nodes and map to fetch promises
        const fetchPromises = currentFlow.nodes
          .filter(node => node.type === OnboardingFlowNodeType.Step)
          .map(async node => {
            const averageTimeSpentData = await getStepAvgTimeSpentWithinTimeFrame({
              flowId: currentFlow._id,
              step: node.name,
              flowVersion: currentFlow.version,
              fromTime: flowFilterDates[0],
              toTime: flowFilterDates[1],
              tags: tagIds,
            });

            const conversionRateData = await getStepConversionRateWithinTimeFrame({
              flowId: currentFlow._id,
              step: node.name,
              flowVersion: currentFlow.version,
              fromTime: flowFilterDates[0],
              toTime: flowFilterDates[1],
              tags: tagIds,
            });

            return {
              name: node.name,
              averageTimeSpentData,
              conversionRateData,
            };
          });

        const stepsData = await Promise.all(fetchPromises);
        const sortedStepsData = [];
        let sortedNodes: OnboardingFlowNode[] = getTopologicallySortedNodesFromFlow(currentFlow);
        sortedNodes = sortedNodes.filter(n => n.type === OnboardingFlowNodeType.Step);
        for (const node of sortedNodes) {
          const stepData = stepsData.find(n => n.name === node.name);
          if (stepData) {
            sortedStepsData.push(stepData);
          }
        }
        setStepData(sortedStepsData);
      } catch (error) {
        toast(`Error retrieving step stats: ${error.message}`, {
          type: 'error',
          autoClose: 5000,
          position: 'top-right',
        });
      } finally {
        setIsLoadingStepStats(false);
      }
    })();
  }, [
    currentFlow._id,
    currentFlow.version,
    currentFlow.nodes,
    flowFilterDates,
    flowFilterTags,
    currentFlow,
  ]);

  useEffect(() => {
    setComparisonData(conversionDataByVersion[performanceComparisonVersion]);
  }, [performanceComparisonVersion, conversionDataByVersion]);

  useEffect(() => {
    if (userTraversal?.userId && userTraversal?.flowId) {
      (async () => {
        try {
          setIsLoadingUserMetrics(true);
          const result = await fetchUserPathMetrics(userTraversal.flowId, userTraversal.userId);
          setUserTimeSpentSteps(result.timeSpentSteps);
        } catch (error) {
          toast(`Error retrieving user path metrics: ${error.message}`, {
            type: 'error',
            autoClose: 5000,
            position: 'top-right',
          });
        } finally {
          setIsLoadingUserMetrics(false);
        }
      })();
    }
  }, [userTraversal?.flowId, userTraversal?.userId]);

  const handleSaveExternalFlowName = async () => {
    try {
      await updateContributorFacingName(currentFlow._id, contributorFacingFlowName);
      editDraftContributorFacingName(currentFlow._id, contributorFacingFlowName);
      toast(`Successfully updated external flow name`, {
        type: 'success',
        autoClose: 5000,
        position: 'top-right',
      });
    } catch (error) {
      toast(`Error saving external flow name: ${error.message}`, {
        type: 'error',
        autoClose: 5000,
        position: 'top-right',
      });
    }
  };

  const handleSaveExternalFlowDescription = async () => {
    try {
      await updateContributorFacingDescription(currentFlow._id, contributorFacingFlowDescription);
      editDraftContributorFacingDescription(currentFlow._id, contributorFacingFlowDescription);
      toast(`Successfully updated external flow description`, {
        type: 'success',
        autoClose: 5000,
        position: 'top-right',
      });
    } catch (error) {
      toast(`Error saving external flow description: ${error.message}`, {
        type: 'error',
        autoClose: 5000,
        position: 'top-right',
      });
    }
  };

  const {
    editDraftPodType,
    editDraftContributorFacingName,
    editDraftContributorFacingDescription,
  } = useDraftFlowStore(state => ({
    editDraftPodType: state.editDraftPodType,
    editDraftContributorFacingName: state.editDraftContributorFacingName,
    editDraftContributorFacingDescription: state.editDraftContributorFacingDescription,
  }));

  const handleSavePodType = async (newPodType: any) => {
    try {
      await updatePodType(currentFlow._id, newPodType);
      editDraftPodType(currentFlow._id, newPodType);
      setPodType(newPodType);
      toast(`Successfully updated pod type`, {
        type: 'success',
        autoClose: 5000,
        position: 'top-right',
      });
    } catch (error) {
      toast(`Error saving pod type: ${error.message}`, {
        type: 'error',
        autoClose: 5000,
        position: 'top-right',
      });
    }
  };

  const formatAverageTimeSpent = (timeSpent?: number) => {
    return timeSpent
      ? moment.duration(Math.round(timeSpent), 'minutes').format('h [hr] m [min]')
      : '—';
  };

  if (
    isLoadingFlowStats ||
    isLoadingStepStats ||
    isLoadingUserMetrics ||
    isLoadingVersionComparisons
  ) {
    return (
      <div className="flex justify-center items-center h-full">
        <Spinner size="sm" className="m-2" />
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      {currentTab === 'Flow Details' && (
        <div>
          <PanelEntry title="Flow ID">
            <div className="flex justify-between items-center">
              <Typography.Overline2 className="p-2">{currentFlow._id}</Typography.Overline2>
              <CopyToClipboard text={currentFlow._id}>
                <Icon icon="copy" variant="neutral" />
              </CopyToClipboard>
            </div>
          </PanelEntry>
          <PanelEntry title="Contributor Facing Name*">
            <div className="flex items-center">
              {editMode && currentFlow ? (
                <div className="flex flex-grow items-center">
                  <TextField
                    id="external-flow-name"
                    placeholder="Contributor Facing Flow Name"
                    value={contributorFacingFlowName}
                    onChange={e => setContributorFacingFlowName(e.target.value)}
                    onBlur={handleSaveExternalFlowName}
                    onKeyPress={e => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleSaveExternalFlowName();
                      }
                    }}
                    className="flex-grow mr-2"
                  />
                  {editMode && (
                    <Icon
                      rounded
                      variant="neutral"
                      icon="check"
                      onClick={handleSaveExternalFlowName}
                    />
                  )}
                </div>
              ) : (
                <Typography.Overline2 className="p-2 flex-grow">
                  {contributorFacingFlowName}
                </Typography.Overline2>
              )}
              {!editMode && currentFlow && (
                <Icon rounded variant="neutral" icon="pencil" onClick={handleStartEdit} />
              )}
            </div>
          </PanelEntry>
          <PanelEntry title="Contributor Facing Description*">
            <div className="flex items-center">
              {editMode && currentFlow ? (
                <div className="flex flex-grow items-center">
                  <TextField
                    id="external-flow-description"
                    placeholder="Contributor Facing Flow Description"
                    value={contributorFacingFlowDescription}
                    onChange={e => setContributorFacingFlowDescription(e.target.value)}
                    onBlur={handleSaveExternalFlowDescription}
                    onKeyPress={e => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleSaveExternalFlowDescription();
                      }
                    }}
                    className="flex-grow mr-2"
                  />
                  {editMode && (
                    <Icon
                      rounded
                      variant="neutral"
                      icon="check"
                      onClick={handleSaveExternalFlowDescription}
                    />
                  )}
                </div>
              ) : (
                <Typography.Overline2 className="p-2 flex-grow">
                  {contributorFacingFlowDescription}
                </Typography.Overline2>
              )}
              {!editMode && currentFlow && (
                <Icon rounded variant="neutral" icon="pencil" onClick={handleStartEdit} />
              )}
            </div>
          </PanelEntry>
          <PanelEntry title="Pod Type*">
            <SelectMenu
              options={podTypeOptions}
              selectedOptionId={podType}
              setSelected={newPodType => handleSavePodType(newPodType)}
              disabled={!editMode}
              menuDirection="top"
            />
          </PanelEntry>
          <PanelEntry title="">
            <Typography.Paragraph3 className="-mt-2">
              * These fields don't need to be published to reflect in OCP. They are automatically
              updated once changed.
            </Typography.Paragraph3>
          </PanelEntry>
        </div>
      )}

      {currentTab === 'Metrics' && (
        <div className="overflow-scroll">
          <PanelEntry title="Summary">
            <div className="flex w-full justify-between space-x-4">
              {[
                { label: 'Currently in flow', value: flowStats.totalActiveUsers },
                { label: 'Entered Flow', value: flowStats.totalEnteredUsers },
                { label: 'Exited Flow', value: flowStats.totalExitedUsers },
              ].map((metric, index) => (
                <div
                  key={index}
                  className="flex flex-col items-start p-4 border border-neutral-200 rounded-lg justify-center space-y-2 flex-grow"
                >
                  <Typography.Overline2 className="text-neutral-500 w-full">
                    {metric.label}
                  </Typography.Overline2>
                  <Typography.H5 className="text-neutral-500 w-full">{metric.value}</Typography.H5>
                </div>
              ))}
            </div>
          </PanelEntry>

          {!!userTimeSpentSteps.length && (
            <PanelEntry title={`User Time in Flow (${searchText || userTraversal?.userId})`}>
              <div className="grid grid-cols-3 items-center gap-4 mb-4">
                <div className="text-left">Step Name</div>
                <div className="text-left">Time Spent</div>
                <div className="text-left">Avg Time Spent</div>
              </div>

              {userTimeSpentSteps.map((step, i) => (
                <div
                  key={i}
                  className="grid grid-cols-3 gap-4 items-center mb-4 pb-4 border-b border-neutral-200"
                >
                  <div className="text-left">
                    <Typography.Paragraph3>{step.step}</Typography.Paragraph3>
                  </div>
                  <div className="text-left">
                    <Typography.Paragraph3>
                      {step.timeSpentSecs < 60
                        ? `${Math.floor(step.timeSpentSecs)} s`
                        : step.timeSpentSecs > 24 * 60 * 60
                        ? moment
                            .duration(step.timeSpentSecs, 'seconds')
                            .format('d [day]', { trim: 'all' })
                        : moment
                            .duration(step.timeSpentSecs, 'seconds')
                            .format('h [hr] m [min] s [s]', { trim: 'all' })}
                    </Typography.Paragraph3>
                  </div>
                  <div className="text-left">
                    <Typography.Paragraph3>
                      {stepData
                        ? stepData.find(n => n.name === step.step)?.averageTimeSpentData.result
                          ? moment
                              .duration(
                                Math.round(
                                  stepData.find(n => n.name === step.step)?.averageTimeSpentData
                                    .result ?? 0,
                                ),
                                'minutes',
                              )
                              .format('h [hr] m [min]')
                          : 'N/A'
                        : 'N/A'}
                    </Typography.Paragraph3>
                  </div>
                </div>
              ))}
            </PanelEntry>
          )}

          <PanelEntry title={`Performance By Step (v${currentFlow.version})`}>
            <div
              className="mb-5"
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'baseline',
              }}
            >
              <div>
                <div style={{ display: 'flex', alignItems: 'center' }} className="mb-2">
                  <SelectMenu
                    optionClassName="cursor-pointer"
                    options={releasedVersions}
                    selectedOptionId={performanceComparisonVersion}
                    setSelected={id => setPerformanceComparisonVersion(id)}
                  />
                  <div className="mx-2">{`vs.`}</div>
                  <Typography.Paragraph1>{`v${currentFlow.version} (current)`}</Typography.Paragraph1>
                </div>
                <div className="mx-1">
                  <Typography.Paragraph3>
                    Versions with no conversion metrics are not available for comparison
                  </Typography.Paragraph3>
                </div>
              </div>

              <SegmentedButtons
                selected={stepPerformanceMetricsView}
                onSelect={setStepPerformanceMetricsView}
                buttons={[
                  { id: 'Time', label: 'Time', icon: ['far', 'clock'] },
                  { id: 'Conversion', label: 'Conversion', icon: ['far', 'percentage'] },
                ]}
              />
            </div>

            {
              <div className="grid grid-cols-4 items-center gap-4 mb-2">
                <div className="text-left text-sm">Step Name</div>
                {stepPerformanceMetricsView === 'Conversion' ? (
                  <>
                    <Tooltip
                      text={'All time conversion data'}
                      target={
                        <div className="flex flex-row" style={{ width: '8vw' }}>
                          <div className="text-left text-sm">{`V${performanceComparisonVersion} Conversion`}</div>
                          <FontAwesomeIcon
                            icon={['far', 'info-circle']}
                            size="xs"
                            className="mx-1"
                          />
                        </div>
                      }
                    />
                    <Tooltip
                      text={'Conversion data for selected date range'}
                      target={
                        <div className="flex flex-row" style={{ width: '8vw' }}>
                          <div className="text-left text-sm">{`V${currentFlow.version} Conversion`}</div>
                          <FontAwesomeIcon
                            icon={['far', 'info-circle']}
                            size="xs"
                            className="mx-1"
                          />
                        </div>
                      }
                    />
                  </>
                ) : (
                  <>
                    <Tooltip
                      text={'All time average'}
                      target={
                        <div className="flex flex-row" style={{ width: '8vw' }}>
                          <div className="text-left text-sm">{`V${performanceComparisonVersion} Avg Time`}</div>
                          <FontAwesomeIcon
                            icon={['far', 'info-circle']}
                            size="xs"
                            className="mx-1"
                          />
                        </div>
                      }
                    />
                    <Tooltip
                      text={'Average for selected date range'}
                      target={
                        <div className="flex flex-row" style={{ width: '8vw' }}>
                          <div className="text-left text-sm">{`V${currentFlow.version} Avg Time`}</div>
                          <FontAwesomeIcon
                            icon={['far', 'info-circle']}
                            size="xs"
                            className="mx-1"
                          />
                        </div>
                      }
                    />
                  </>
                )}
                <div className="text-left text-sm">Delta</div>
              </div>
            }

            {stepData.map((node, index) => (
              <div
                key={index}
                className="grid grid-cols-4 gap-4 items-center mb-3 pb-4 border-b border-neutral-200"
              >
                <div className="text-left">
                  <Typography.Paragraph3>{node.name}</Typography.Paragraph3>
                </div>

                {stepPerformanceMetricsView === 'Conversion' ? (
                  <>
                    <div style={{ width: '7vw', height: '1.5lh' }}>
                      {comparisonData && comparisonData.at(index)?.conversionRateData.result ? (
                        <div
                          style={conversionBarStyle(
                            Math.round(comparisonData.at(index)?.conversionRateData.result! * 100),
                          )}
                        >
                          <div className="text-xs" style={{ margin: 'auto 0.5vw' }}>
                            {`${Math.round(
                              comparisonData.at(index)?.conversionRateData.result! * 100,
                            )}%`}
                          </div>
                        </div>
                      ) : (
                        <Typography.Paragraph3>{'—'}</Typography.Paragraph3>
                      )}
                    </div>

                    <div style={{ width: '7vw', height: '1.5lh' }}>
                      {node.conversionRateData.result ? (
                        <div
                          style={conversionBarStyle(
                            Math.round(node.conversionRateData.result * 100),
                          )}
                        >
                          <div className="text-xs" style={{ margin: 'auto 0.5vw' }}>
                            {`${Math.round(node.conversionRateData.result * 100)}%`}
                          </div>
                        </div>
                      ) : (
                        <Typography.Paragraph3>{'—'}</Typography.Paragraph3>
                      )}
                    </div>

                    <div>
                      {node.conversionRateData.result &&
                      comparisonData &&
                      comparisonData.at(index)?.conversionRateData.result ? (
                        <div className="flex flex-row">
                          <FontAwesomeIcon
                            className="mx-1"
                            icon={[
                              'far',
                              comparisonData.at(index)?.conversionRateData.result! ===
                              node.conversionRateData.result
                                ? 'equals'
                                : comparisonData.at(index)?.conversionRateData.result! <
                                  node.conversionRateData.result
                                ? 'arrow-up'
                                : 'arrow-down',
                            ]}
                          />
                          <Typography.Paragraph3>
                            {`${Math.abs(
                              Math.round(
                                comparisonData.at(index)?.conversionRateData.result! * 100,
                              ) - Math.round(node.conversionRateData.result * 100),
                            )}%`}
                          </Typography.Paragraph3>
                        </div>
                      ) : (
                        <Typography.Paragraph3 className="mx-1">{'—'}</Typography.Paragraph3>
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    {comparisonData && comparisonData.at(index)?.averageTimeSpentData.result ? (
                      <Typography.Paragraph3>
                        {formatAverageTimeSpent(
                          comparisonData.at(index)?.averageTimeSpentData.result,
                        )}
                      </Typography.Paragraph3>
                    ) : (
                      <Typography.Paragraph3>{'—'}</Typography.Paragraph3>
                    )}
                    <div className="text-left">
                      <Typography.Paragraph3>
                        {formatAverageTimeSpent(node.averageTimeSpentData.result)}
                      </Typography.Paragraph3>
                    </div>

                    <div>
                      {node.averageTimeSpentData.result &&
                      comparisonData &&
                      comparisonData.at(index)?.averageTimeSpentData.result ? (
                        <div className="flex flex-row">
                          <FontAwesomeIcon
                            className="mx-1"
                            icon={[
                              'far',
                              Math.round(comparisonData.at(index)?.averageTimeSpentData.result!) ===
                              Math.round(node.averageTimeSpentData.result)
                                ? 'equals'
                                : comparisonData.at(index)?.averageTimeSpentData.result! <
                                  node.averageTimeSpentData.result
                                ? 'arrow-up'
                                : 'arrow-down',
                            ]}
                          />
                          <Typography.Paragraph3>
                            {`${Math.abs(
                              Math.round(
                                ((Math.round(
                                  comparisonData.at(index)?.averageTimeSpentData.result!,
                                ) -
                                  Math.round(node.averageTimeSpentData.result)) /
                                  comparisonData.at(index)?.averageTimeSpentData.result!) *
                                  100,
                              ),
                            )}%`}
                          </Typography.Paragraph3>
                        </div>
                      ) : (
                        <Typography.Paragraph3 className="mx-1">{'—'}</Typography.Paragraph3>
                      )}
                    </div>
                  </>
                )}
              </div>
            ))}
          </PanelEntry>
        </div>
      )}
    </div>
  );
};

export default DetailPanelFlowOverview;
